import { supabase } from '../lib/supabase';
import { emailService } from './emailService';

export const subscriptionService = {
  /**
   * Subscribe with email only to subscribers table (hero form)
   * @param {string} email - Email address
   * @returns {Promise<{success: boolean, message: string, data?: object}>}
   */
  async subscribeEmailOnly(email) {
    try {
      // Check if email already exists in subscribers table
      const { data: existing, error: checkError } = await supabase
        ?.from('subscribers')
        ?.select('id, email')
        ?.eq('email', email?.toLowerCase()?.trim())
        ?.single();

      if (checkError && checkError?.code !== 'PGRST116') {
        throw checkError;
      }

      if (existing) {
        return {
          success: false,
          message: 'This email is already subscribed to our newsletter.'
        };
      }

      // Insert into subscribers table with email only
      const { data, error } = await supabase
        ?.from('subscribers')
        ?.insert({
          email: email?.toLowerCase()?.trim(),
          first_name: null,
          last_name: null,
          organization: null
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Try to send welcome email (don't fail subscription if email fails)
      try {
        const emailResult = await emailService?.sendWelcomeEmail(email, null);
        
        if (!emailResult?.success) {
          console.warn('Welcome email failed to send:', emailResult?.error);
        }
      } catch (emailError) {
        // Log email failure but don't fail the subscription
        console.error('Email service exception:', emailError);
      }

      return {
        success: true,
        message: 'Successfully subscribed! Check your inbox for our welcome email.',
        data
      };
    } catch (error) {
      console.error('Subscription error:', error);
      return {
        success: false,
        message: error?.message || 'An error occurred. Please try again.'
      };
    }
  },

  /**
   * Subscribe with email only (simple form) or full details (detailed form)
   * @param {string|object} emailOrData - Email string or object with subscriber details
   * @returns {Promise<{success: boolean, message: string, data?: object}>}
   */
  async subscribe(emailOrData) {
    try {
      // Determine if simple email or detailed form
      const isSimpleForm = typeof emailOrData === 'string';
      
      if (isSimpleForm) {
        // Simple email subscription to newsletter_subscriptions table
        const email = emailOrData;
        
        // Check if email already exists in newsletter_subscriptions
        const { data: existing, error: checkError } = await supabase?.from('newsletter_subscriptions')?.select('id, is_active')?.eq('email', email)?.single();

        if (checkError && checkError?.code !== 'PGRST116') {
          throw checkError;
        }

        if (existing) {
          if (existing?.is_active) {
            return {
              success: false,
              message: 'This email is already subscribed to our newsletter.'
            };
          } else {
            // Reactivate subscription
            const { error: updateError } = await supabase?.from('newsletter_subscriptions')?.update({
                is_active: true,
                subscribed_at: new Date()?.toISOString(),
                unsubscribed_at: null
              })?.eq('id', existing?.id);

            if (updateError) throw updateError;

            // Try to send welcome email (don't fail subscription if email fails)
            try {
              const emailResult = await emailService?.sendWelcomeEmail(email, null);
              
              if (!emailResult?.success) {
                console.warn('Welcome email failed to send:', emailResult?.error);
              }
            } catch (emailError) {
              console.error('Email service exception:', emailError);
            }

            return {
              success: true,
              message: 'Welcome back! Your subscription has been reactivated.'
            };
          }
        }

        // Create new simple subscription
        const { data, error } = await supabase?.from('newsletter_subscriptions')?.insert({
            email: email?.toLowerCase()?.trim()
          })?.select()?.single();

        if (error) throw error;

        // Try to send welcome email (don't fail subscription if email fails)
        try {
          const emailResult = await emailService?.sendWelcomeEmail(email, null);
          
          if (!emailResult?.success) {
            console.warn('Welcome email failed to send:', emailResult?.error);
          }
        } catch (emailError) {
          console.error('Email service exception:', emailError);
        }

        return {
          success: true,
          message: 'Successfully subscribed! Check your inbox for our welcome email.',
          data
        };
      } else {
        // Detailed form subscription to subscribers table
        const { email, firstName, lastName, organization } = emailOrData;
        
        // Check if email already exists in subscribers table
        const { data: existing, error: checkError } = await supabase?.from('subscribers')?.select('id, email')?.eq('email', email)?.single();

        if (checkError && checkError?.code !== 'PGRST116') {
          throw checkError;
        }

        if (existing) {
          return {
            success: false,
            message: 'This email is already registered in our system.'
          };
        }

        // Create new detailed subscription (snake_case for database)
        const { data, error } = await supabase?.from('subscribers')?.insert({
            email: email?.toLowerCase()?.trim(),
            first_name: firstName?.trim() || null,
            last_name: lastName?.trim() || null,
            organization: organization?.trim() || null
          })?.select()?.single();

        if (error) throw error;

        // Try to send welcome email (don't fail subscription if email fails)
        try {
          const emailResult = await emailService?.sendWelcomeEmail(email, firstName);
          
          if (!emailResult?.success) {
            console.warn('Welcome email failed to send:', emailResult?.error);
          }
        } catch (emailError) {
          console.error('Email service exception:', emailError);
        }

        return {
          success: true,
          message: 'Thank you for subscribing! You will receive our latest funding opportunities and research insights.',
          data
        };
      }
    } catch (error) {
      console.error('Subscription error:', error);
      return {
        success: false,
        message: error?.message || 'An error occurred. Please try again.'
      };
    }
  },

  /**
   * Check if an email is subscribed
   * @param {string} email - Email address to check
   * @returns {Promise<boolean>}
   */
  async isSubscribed(email) {
    try {
      // Check both tables
      const { data: simpleData } = await supabase?.from('newsletter_subscriptions')?.select('is_active')?.eq('email', email?.toLowerCase()?.trim())?.single();

      const { data: detailedData } = await supabase?.from('subscribers')?.select('id')?.eq('email', email?.toLowerCase()?.trim())?.single();

      return (simpleData?.is_active) || (detailedData?.id != null);
    } catch (error) {
      console.error('Check subscription error:', error);
      return false;
    }
  }
};